import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useLazyQuery, gql } from "@apollo/client";
import { isToday, parse, isAfter } from "date-fns";

const GET_SERVICES = gql`
  query getAdminService($data: Filter!) {
    getAdminService(data: $data) {
      services {
        id
        name
        price
        specialPrice
        firstTimePrice
        gender
        time
      }
    }
  }
`;

const SEND_ADD_ON_OTP = gql`
  mutation sendAddOnOtp($data: SendAddOnOtpInput!) {
    sendAddOnOtp(data: $data) {
      status
      message
      orderId
      devOtp
    }
  }
`;

const EDIT_BOOKING = gql`
  mutation editBooking($data: editBookingInput) {
    editBooking(data: $data) {
      status
      message
      order {
        id
        price
        totalPrice
        status
        address
        bookingDate
        timeSlot
        paymentStatus
        pincode
        paymentType
        verificationStatus
        femaleTimeSlot
        bookingId
        bookingType
        adminPriceAdjustment
        adminPriceAdjustmentReason
        couponDiscount
        codCharges
        couponCode
        orderDetail {
          id
          name
          serviceId
          price
          specialPrice
          firstTimePrice
          cutPrice
          count
          type
        }
        employee {
          id
          fullName
        }
        user {
          id
          fullName
          contactNumber
        }
      }
    }
  }
`;
const GET_AVAILABLE_SLOTS = gql`
  query getAvailableSlots($data: getAvailableSlotsInput) {
    getAvailableSlots(data: $data) {
      slots
      employeeIds
    }
  }
`;

const generateNext7Days = () => {
  const today = new Date();
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    const value = date.toISOString().slice(0, 10);
    const label = date.toLocaleDateString("en-IN", {
      day: "2-digit",
      weekday: "short",
    });
    return { value, label };
  });
};

const toNumericTime = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const mapOrderServices = (orderDetail = [], services = []) => {
  return orderDetail.map((service) => {
    const matchedService = services.find(
      (catalogService) => catalogService.id === service.serviceId,
    );

    return {
      id: service.serviceId,
      name: service.name,
      count: service.count,
      price: service.price,
      type: service.type,
      time: toNumericTime(matchedService?.time ?? service.time),
    };
  });
};

// Started-or-beyond statuses - matches the backend's own gate
// (editBooking's isOnSiteAddOn check: changeType === "Service Addition" &&
// order.serviceStarted) so this modal is only shown when the OTP will
// actually be required, not on every service edit.
const STARTED_OR_BEYOND_STATUSES = ["Started", "Scanned", "Completed"];

const AddOnOtpModal = ({ onSubmit, onCancel, submitting, error }) => {
  const [otp, setOtp] = useState("");

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60">
      <div className="bg-[#1a1a1a] border border-[#333] rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
        <h3 className="text-lg font-bold text-[#FBB034] mb-1">Confirm add-on with customer</h3>
        <p className="text-sm text-gray-300 mb-4">
          We just sent a code to the customer. Ask them for it to confirm this add-on.
        </p>
        <input
          type="text"
          maxLength={4}
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
          placeholder="4-digit code"
          className="w-full px-3 py-2 rounded-lg bg-[#0c0b0b] border border-[#444] text-white text-center text-xl tracking-widest mb-3"
        />
        {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="flex-1 px-4 py-2 rounded-lg text-sm text-gray-300 border border-[#444] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSubmit(otp)}
            disabled={submitting || otp.length < 4}
            className="flex-1 px-4 py-2 rounded-lg text-sm font-semibold bg-[#FBB034] text-black disabled:opacity-50"
          >
            {submitting ? "Confirming..." : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
};

const EditBooking = ({ editingData, order, onOrderUpdated, onError }) => {
  const [editBooking] = useMutation(EDIT_BOOKING);
  const [sendAddOnOtp] = useMutation(SEND_ADD_ON_OTP);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [otpError, setOtpError] = useState("");
  // Holds the finalDataObj that's ready to submit as soon as the OTP is
  // confirmed, so the OTP modal doesn't need to re-derive it.
  const [pendingSubmission, setPendingSubmission] = useState(null);
  const { data: servicesData, loading: servicesLoading } = useQuery(
    GET_SERVICES,
    {
      variables: {
        data: { gender: editingData?.gender, order: "", search: "" },
      },
    },
  );
  const [
    getAvailableSlots,
    { data: availableSlotsData, loading: slotsLoading },
  ] = useLazyQuery(GET_AVAILABLE_SLOTS);

  const [skipSlotValidation, setSkipSlotValidation] = useState(false);
  // On a started booking the admin chooses how the change is recorded:
  // "addon" (customer approves with a code, tracked as an add-on) or
  // "correction" (fixing a mistake - reason required, no code, not an add-on).
  const [changeMode, setChangeMode] = useState("addon");
  const [correctionReason, setCorrectionReason] = useState("");
  const isStartedBooking = STARTED_OR_BEYOND_STATUSES.includes(order.status);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(editingData.timeSlot);
  const [selectedSlot, setSelectedSlot] = useState(editingData.timeSlot || "");
  const [slots, setSlots] = useState([]);
  const [serviceSearchTerm, setServiceSearchTerm] = useState("");
  const [filteredServices, setFilteredServices] = useState([]);
  const [servicesList, setServicesList] = useState([]);
  const [selectedServices, setSelectedServices] = useState(
    mapOrderServices(editingData.orderDetail),
  );

  const filteredSlots = useMemo(() => {
    if (!date) return slots;
    // if (isToday(new Date(date))) {
    //   const now = new Date();
    //   return slots.filter((slot) =>
    //     isAfter(parse(slot, "HH:mm", new Date()), now)
    //   );
    // }
    return slots;
  }, [slots, date]);

  useEffect(() => {
    setTime(editingData.timeSlot);
    setSelectedSlot(editingData.timeSlot || "");
    setSelectedServices(
      mapOrderServices(editingData.orderDetail, servicesList),
    );
  }, [editingData, servicesList]);

  useEffect(() => {
    if (availableSlotsData?.getAvailableSlots) {
      setSlots(availableSlotsData.getAvailableSlots.slots);
    }
  }, [availableSlotsData]);

  useEffect(() => {
    if (servicesData?.getAdminService?.services) {
      const nextServices = servicesData.getAdminService.services;
      setServicesList(nextServices);
      setSelectedServices((prev) =>
        prev.map((service) => {
          const matchedService = nextServices.find(
            (catalogService) => catalogService.id === service.id,
          );

          return matchedService
            ? { ...service, time: toNumericTime(matchedService.time) }
            : { ...service, time: toNumericTime(service.time) };
        }),
      );
    }
  }, [servicesData]);

  useEffect(() => {
    if (serviceSearchTerm.trim() === "") {
      setFilteredServices([]);
      return;
    }
    const filtered = servicesList.filter((service) =>
      service.name.toLowerCase().includes(serviceSearchTerm.toLowerCase()),
    );
    setFilteredServices(filtered);
  }, [serviceSearchTerm, servicesList]);

  const handleToggleSkip = () => {
    setSkipSlotValidation((prev) => !prev);
    setSelectedSlot("");
    setDate(null);
    setTime("");
  };

  const handleServiceSelect = (service) => {
    setSelectedServices((prev) => {
      const exists = prev.find((s) => s.id === service.id);
      if (exists) {
        return prev.map((s) =>
          s.id === service.id ? { ...s, count: s.count + 1 } : s,
        );
      }
      return [
        ...prev,
        {
          ...service,
          count: 1,
          type: "basic",
          time: toNumericTime(service.time),
        },
      ];
    });
    setDate("");
    setTime("");
    setSelectedSlot("");
  };

  const handleServiceRemove = (id) => {
    setSelectedServices((prev) =>
      prev
        .map((s) => (s.id === id ? { ...s, count: s.count - 1 } : s))
        .filter((s) => s.count > 0),
    );
    setDate("");
    setTime("");
    setSelectedSlot("");
  };

  // Shared by the direct path (no OTP needed) and the post-OTP-confirm
  // path. `adjustedServices` is only needed for the optimistic-update
  // fallback when the server doesn't echo back a full order.
  const submitEditBooking = async (dataObj, adjustedServices) => {
    try {
      const res = await editBooking({ variables: { data: dataObj } });
      if (res.data.editBooking.status === "SUCCESS") {
        const updatedOrder = res.data.editBooking.order || {
          ...order,
          bookingDate: dataObj.bookingDate,
          timeSlot: dataObj.timeSlot,
          orderDetail: adjustedServices.map((s) => ({
            serviceId: s.id,
            name: s.name,
            count: s.count,
            price: s.price,
            type: s.type,
          })),
        };

        onOrderUpdated?.(updatedOrder);
        onError?.("");
        alert(res.data.editBooking.message || "Booking updated");
        return true;
      }
      return res.data.editBooking.message || "Something went wrong";
    } catch (error) {
      console.error(error);
      return "Failed to update booking";
    }
  };

  const handleUpdate = async (e) => {
    e?.preventDefault?.();
    onError?.("");
    if (selectedServices.length === 0) {
      alert("Please select at least one service");
      return;
    }
    if (!date) {
      alert("Please select a booking date");
      return;
    }
    if (!selectedSlot) {
      alert("Please select a time slot");
      return;
    }

    const originalCountMap = {};
    (editingData.orderDetail || []).forEach((s) => {
      originalCountMap[s.serviceId] = s.count;
    });

    const adjustedServices = selectedServices.map((s) => {
      const originalCount = originalCountMap[s.id] || 0;
      if (s.count < originalCount) {
        return { ...s, type: "basic" };
      }
      return s;
    });

    const expandedServiceList = adjustedServices.flatMap((s) =>
      Array.from({ length: s.count }).map(() => ({
        id: s.id,
        name: s.name,
        price: s.price,
        type: s.type,
      })),
    );

    // Only tagged as a "Service Addition" when a service's count actually
    // went up - a pure reschedule shouldn't trip the add-on OTP gate.
    const isAddingNewService = selectedServices.some(
      (s) => s.count > (originalCountMap[s.id] || 0),
    );

    const isCorrection = isStartedBooking && changeMode === "correction";
    if (isCorrection && !correctionReason.trim()) {
      alert("Please enter a reason for the correction");
      return;
    }

    const finalDataObj = {
      orderId: order.id,
      bookingDate: date,
      timeSlot: selectedSlot || time,
      updatedServiceList: expandedServiceList,
      skipSlotValidation,
      ...(isCorrection
        ? { changeType: "Admin Correction", changeReason: correctionReason.trim() }
        : isAddingNewService
          ? { changeType: "Service Addition" }
          : {}),
    };

    // Corrections never need the customer's code - the backend accepts them
    // from admins with a reason, and doesn't count them as add-ons.
    if (isCorrection) {
      const result = await submitEditBooking(finalDataObj, adjustedServices);
      if (result !== true) onError?.(result);
      return;
    }

    // The backend only requires addOnOtp when it's genuinely an on-site
    // add-on (Service Addition on an already-started order) - matches
    // editBooking's own isOnSiteAddOn check exactly, so we don't send a
    // customer an OTP that will never actually get checked.
    const needsAddOnOtp =
      isAddingNewService && STARTED_OR_BEYOND_STATUSES.includes(order.status);

    if (needsAddOnOtp) {
      setOtpError("");
      // Send the exact list being saved - the backend prices it for the
      // customer's message and only accepts this same list with the code.
      const otpRes = await sendAddOnOtp({
        variables: {
          data: { orderId: order.id, updatedServiceList: expandedServiceList },
        },
      });
      if (otpRes.data?.sendAddOnOtp?.status !== "SUCCESS") {
        onError?.(otpRes.data?.sendAddOnOtp?.message || "Could not send OTP");
        return;
      }
      setPendingSubmission({ finalDataObj, adjustedServices });
      setShowOtpModal(true);
      return;
    }

    const result = await submitEditBooking(finalDataObj, adjustedServices);
    if (result !== true) {
      onError?.(result);
    }
  };

  const handleOtpConfirm = async (otp) => {
    if (!pendingSubmission) return;
    setOtpSubmitting(true);
    setOtpError("");
    const result = await submitEditBooking(
      { ...pendingSubmission.finalDataObj, addOnOtp: otp },
      pendingSubmission.adjustedServices,
    );
    setOtpSubmitting(false);
    if (result === true) {
      setShowOtpModal(false);
      setPendingSubmission(null);
    } else {
      // Wrong/expired OTP etc. - keep the modal open so admin can retry
      // without re-triggering the whole edit flow.
      setOtpError(result);
    }
  };

  const handleDateSelect = (value) => {
    setSelectedSlot("");
    setTime("");
    const totalTime = selectedServices.reduce(
      (sum, service) =>
        sum + toNumericTime(service.time) * Number(service.count || 0),
      0,
    );

    if (!totalTime) {
      onError?.("Could not calculate service time for selected services.");
      setDate(value);
      return;
    }

    getAvailableSlots({
      variables: {
        data: {
          date: value,
          femaleTimeSlot: order?.femaleTimeSlot,
          serviceTime: totalTime.toString(),
          skipSlotValidation,
          employeeId: order?.employee?.id || null,
          orderId: order.id,
        },
      },
      fetchPolicy: "network-only",
    });
    setDate(value);
    onError?.("");
  };

  return (
    <>
      {/* Assigned Employee - Read Only */}
      <div className="flex flex-col sm:flex-row sm:items-center mt-4">
        <span className="text-white text-sm w-full sm:w-1/6">Assigned Employee:</span>
        <span className="text-[#FBB034] font-bold w-full sm:w-4/6">
          {order?.employee?.fullName || "No Employee Assigned"}
        </span>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center mt-4">
        <span className="text-white text-sm w-full sm:w-1/6">Current Booking Slot:</span>
        <span className="text-[#FBB034] font-bold w-full sm:w-4/6">
          {order.bookingDate} {order?.timeSlot || "No Slot Assigned"}
        </span>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mt-4">
        <div className="flex items-center gap-3">
          <label className="text-white text-sm">
            Book without slot validation:
          </label>
          <button
            onClick={handleToggleSkip}
            className={`px-3 py-1 rounded ${
              skipSlotValidation
                ? "bg-[#FBB034] text-black"
                : "bg-[#0c0b0b] text-white border border-[#E0E0E0]"
            }`}
          >
            {skipSlotValidation ? "ON" : "OFF"}
          </button>
        </div>
        <p className="text-gray-400 text-sm">
          When ON, admin must pick an employee manually and booking will be
          created without slot validation/assignment.
        </p>
      </div>

      {/* Service Selection */}
      <div className="flex flex-col gap-2 mt-4">
        <input
          type="text"
          placeholder="Search services..."
          value={serviceSearchTerm}
          onChange={(e) => setServiceSearchTerm(e.target.value)}
          className="bg-[#0c0b0b] text-white border-b border-[#E0E0E0] px-2 py-1 w-full"
        />
        {servicesLoading ? (
          <p className="text-gray-400 text-center">Loading services...</p>
        ) : (
          <div className="flex flex-wrap gap-2 mt-2">
            {filteredServices.map((service) => (
              <button
                key={service.id}
                onClick={() => handleServiceSelect(service)}
                className={`px-3 py-1 rounded ${
                  selectedServices.find((s) => s.id === service.id)
                    ? "bg-[#FBB034] text-black"
                    : "bg-[#0c0b0b] text-white border border-[#E0E0E0]"
                }`}
              >
                {service.name} (₹{service.price})
              </button>
            ))}
          </div>
        )}
        {selectedServices.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {selectedServices.map((service) => (
              <div
                key={service.id}
                className="flex items-center px-2 py-1 bg-[#FBB034] text-black rounded"
              >
                {service.name} (x{service.count})
                <button
                  onClick={() => handleServiceRemove(service.id)}
                  className="ml-2 text-red-600"
                >
                  ❌
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <>
        <div className="flex space-x-2 overflow-x-auto py-2">
          {generateNext7Days().map((day) => (
            <button
              key={day.value}
              onClick={() => handleDateSelect(day.value)}
              className={`px-3 py-2 rounded ${
                date === day.value
                  ? "bg-blue-600 text-white"
                  : "bg-gray-800 text-gray-200"
              }`}
            >
              {day.label}
            </button>
          ))}
        </div>
      </>
      {
        // NOT skipping: show slots
        date ? (
          slotsLoading ? (
            <p className="text-white">Fetching slots...</p>
          ) : filteredSlots.length > 0 ? (
            <div className="flex flex-wrap gap-2 mt-4">
              {filteredSlots.map((slot) => (
                <div
                  key={slot}
                  onClick={() => {
                    setSelectedSlot(slot);
                    setTime(slot);
                  }}
                  className={`px-3 py-2 rounded cursor-pointer transition-colors
                            ${
                              selectedSlot === slot
                                ? "bg-blue-600 text-white"
                                : "bg-green-600 text-white hover:bg-green-700"
                            }
                          `}
                >
                  {slot}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-red-500">No available slots</p>
          )
        ) : null
      }

      {/* How to record a change on a started booking */}
      {isStartedBooking && (
        <div className="mt-6 rounded-lg border border-[#3a3a3a] bg-[#1e1e1e] p-4">
          <p className="text-white text-sm font-semibold mb-3">
            This service has started. How should this change be recorded?
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {[
              {
                value: "addon",
                title: "➕ Add-on",
                body: "Customer is getting an extra service. They approve it with a code, and it's tracked as an add-on.",
              },
              {
                value: "correction",
                title: "✏️ Edit / correction",
                body: "Fixing a mistake on the booking. Needs a reason, no customer code, not counted as an add-on.",
              },
            ].map((opt) => (
              <label
                key={opt.value}
                className={`cursor-pointer rounded-md border p-3 text-sm transition ${
                  changeMode === opt.value
                    ? "border-[#FBB034] bg-[#FBB034]/10"
                    : "border-[#3a3a3a] hover:border-gray-500"
                }`}
              >
                <input
                  type="radio"
                  name="changeMode"
                  value={opt.value}
                  checked={changeMode === opt.value}
                  onChange={() => setChangeMode(opt.value)}
                  className="mr-2 accent-[#FBB034]"
                />
                <span className="text-white font-semibold">{opt.title}</span>
                <p className="mt-1 text-xs text-gray-400">{opt.body}</p>
              </label>
            ))}
          </div>
          {changeMode === "correction" && (
            <div className="mt-3">
              <label className="block text-xs font-semibold text-gray-300 mb-1">
                Reason for correction (required)
              </label>
              <textarea
                rows={2}
                value={correctionReason}
                onChange={(e) => setCorrectionReason(e.target.value)}
                placeholder="e.g. Professional did threading but forgot to add it"
                className="w-full rounded-md border border-[#3a3a3a] bg-[#111] p-2 text-sm text-white"
              />
            </div>
          )}
        </div>
      )}

      {/* Update Button */}
      <div className="flex justify-center py-8">
        <button
          className="bg-[#FBB034] text-sm font-bold px-5 py-2 rounded-md"
          onClick={handleUpdate}
        >
          Update Booking
        </button>
      </div>

      {showOtpModal && (
        <AddOnOtpModal
          onSubmit={handleOtpConfirm}
          onCancel={() => {
            setShowOtpModal(false);
            setPendingSubmission(null);
            setOtpError("");
          }}
          submitting={otpSubmitting}
          error={otpError}
        />
      )}
    </>
  );
};

export default EditBooking;
